import { GroupState } from '@/composition/groups'
import { InvitationState } from '@/composition/invitations'
import { LearningState } from '@/composition/learning'
import { TAB_NAME_TO_NAV_NAME_MAP, TabName } from '@/models/TabConfiguration'
import { useGlobalStore } from '@/store/app'
import { isGokollab } from '@ghl/clientportal-core/init'

export function getCurrentTabDataHelper(
  activeMenuItem,
  currentData,
  originalData
) {
  switch (activeMenuItem) {
    case 'GroupDetails':
      return {
        ...originalData,
        name: currentData.name,
        description: currentData.description,
        slug: currentData.slug,
        settings: {
          ...originalData.settings,
          groupType: currentData.settings?.groupType,
          groupSwitcherVisibility:
            currentData.settings?.groupSwitcherVisibility,
          allowsMembersInvite: currentData.settings?.allowsMembersInvite,
          showMandatoryInfoModal: currentData.settings?.showMandatoryInfoModal,
        },
      }
    case 'GroupSubscriptions':
      return {
        ...originalData,
        settings: {
          ...originalData.settings,
          membership: currentData.settings?.membership
            ? {
                membershipType: currentData.settings.membership.membershipType,
                subscriptionType:
                  currentData.settings.membership.subscriptionType,
                subscriptionInterval:
                  currentData.settings.membership.subscriptionInterval,
                price: currentData.settings.membership.price,
                isLivePayment: currentData.settings.membership.isLivePayment,
                trialDays: currentData.settings.membership.trialDays,
                currency: currentData.settings.membership.currency,
                paymentPriceId:
                  originalData.settings?.membership?.paymentPriceId,
                paymentProductId:
                  originalData.settings?.membership?.paymentProductId,
                locationName: originalData.settings?.membership?.locationName,
              }
            : originalData.settings?.membership,
        },
      }
    case 'GroupBranding':
      return {
        ...originalData,
        branding: currentData.branding,
        settings: {
          ...originalData.settings,
          customCode: currentData.settings?.customCode,
        },
      }
    case 'GroupAppearance':
      return {
        ...originalData,
        branding: {
          ...originalData.branding,
          color: currentData.branding?.color,
        },
      }
    case 'Discovery':
      return {
        ...originalData,
        marketplaceGroupSlug: currentData.marketplaceGroupSlug,
      }
    case 'MembershipQuestions':
      return {
        ...originalData,
        settings: {
          ...originalData.settings,
          membershipQuestionsEnabled:
            currentData.settings?.membershipQuestionsEnabled,
        },
      }
    case 'GroupNewsletter':
      return {
        ...originalData,
        settings: {
          ...originalData.settings,
          newsletterConfig: currentData.settings?.newsletterConfig,
        },
      }
    case 'TabsConfiguration':
      return {
        ...originalData,
        settings: {
          ...originalData.settings,
          tabsConfiguration: currentData.settings?.tabsConfiguration,
        },
      }
    default:
      return currentData
  }
}

export function getGroupSlugFromUrl(url: string) {
  const groupSlug = isGokollab() ? url.split('/')[0] : url.split('/')[2]
  return groupSlug
}

// export function redirectToGroup(groupSlug: string) {
//   const globalStore = useGlobalStore()
//   globalStore.setGroupId('')
//   GroupState.state.groupData = {} as any
//   router.push({ name: 'home', params: { groupSlug: groupSlug } })
// }

export function resetGroupData(): void {
  const globalStore = useGlobalStore()
  GroupState.state.fetchingGroupData = true

  globalStore.setGroupId('')
  GroupState.resetState()
  LearningState.resetState()
  InvitationState.resetState()
  // Set group data loading state to prevent showing old data during group switch
}

// Check if a navigation item is visible based on group settings
export function isTabVisibleByNavigationItemName(
  navigationItemName: string
): boolean {
  const tabsConfiguration =
    GroupState.state.groupData.settings?.tabsConfiguration

  // If no configuration exists, show all tabs (default behavior)
  if (!tabsConfiguration || !Array.isArray(tabsConfiguration)) {
    return true
  }

  // Find the matching tab configuration by comparing navigation item name
  const matchingTabConfig = tabsConfiguration.find(
    tabConfig => TAB_NAME_TO_NAV_NAME_MAP[tabConfig.name] === navigationItemName
  )

  // If no matching config found, show the tab (default behavior)
  if (!matchingTabConfig) {
    return true
  }

  if(matchingTabConfig.name === TabName.MEMBERS && GroupState.isAdminOrOwner()) {
    return true
  }
  
  // Return the isVisible value from configuration
  return matchingTabConfig.isVisible ?? true
}

// Check if a tab is visible based on backend enum value (LEARNING, EVENTS, etc.)
export function isTabVisibleByName(tabName: TabName): boolean {
  const tabsConfiguration =
    GroupState.state.groupData.settings?.tabsConfiguration

  // If no configuration exists, show all tabs (default behavior)
  if (!tabsConfiguration || !Array.isArray(tabsConfiguration)) {
    return true
  }

  // Find the matching tab configuration by enum name
  const matchingTabConfig = tabsConfiguration.find(
    tabConfig => tabConfig.name === tabName
  )

  // If no matching config found, show the tab (default behavior)
  if (!matchingTabConfig) {
    return true
  }

  if(matchingTabConfig.name === TabName.MEMBERS && GroupState.isAdminOrOwner()) {
    return true
  }

  // Return the isVisible value from configuration
  return matchingTabConfig.isVisible ?? true
}
